import React, { useCallback, useState } from 'react';
import { Stack, Text, Button, Overlay } from '@moda/om';
import { formatPrice } from '../../lib/formatPrice';
import { PaymentSource } from '../../constants';
import { useOrderSummary } from '../../hooks/useOrderSummary';
import { useSecondPaymentWithAffirm } from '../../hooks/useAffirmSecondPayment/useSecondPaymentWithAffirm';
import { OrderDetailsApplePayButton } from '../OrderDetailsPaymentSources/OrderDetailsApplePayButton';
import { LoadingBalls } from '../LoadingBalls';
import { PaymentSummaryRow } from './PaymentSummaryRow';
import { PayTodayConfirmationModal } from './PayTodayConfirmationModal';

import './OrderPaymentSummary.scss';

interface Props {
  orderNumber: string;
  hasBillFailure?: boolean;
}

const TAXES_TOOLTIP_COPY =
  'Taxes and duties are calculated according to the applicable laws of the state or country an item is shipped to.';

export const OrderPaymentSummary: React.FC<Props> = ({ orderNumber, hasBillFailure }) => {
  const { orderSummary, orderItems } = useOrderSummary(orderNumber);
  const [loading, setLoading] = useState(false);
  const { handleSecondPaymentWithAffirm } = useSecondPaymentWithAffirm();
  const [payTodayModalOpen, setPayTodayModalOpen] = useState(false);

  const handleButtonClick = useCallback(() => {
    setLoading(true);
    orderSummary?.attributes.affirmSecondChargeEnabled
      ? handleSecondPaymentWithAffirm()
      : setPayTodayModalOpen(true);
  }, [orderSummary, handleSecondPaymentWithAffirm]);

  const handleModalCancel = useCallback(() => {
    setLoading(false);
    setPayTodayModalOpen(false);
  }, []);

  if (!orderSummary || !orderItems) return null;

  const {
    attributes: {
      cardTypeName,
      hasPendingAuthorizationItems,
      paymentMethod: { lastFour, source, isValid },
      paymentSummary: {
        amountToBeCharged,
        costItemGiftCopy,
        discounts,
        gst,
        gstLabel,
        duty,
        dutyLabel,
        payToday,
        remainingBalance,
        rewardsToBeUsed,
        salesTax,
        salesTaxLabel,
        shippingAndPackaging,
        siteCreditToBeUsed,
        subtotal,
        total,
        totalPaid,
        vat,
        vatLabel
      },
      affirmSecondChargeEnabled,
      transactions
    }
  } = orderSummary;

  return (
    <>
      <Stack space={4} className="OrderPaymentSummary">
        <Text treatment="h3" family="serif">
          Payment Summary
        </Text>

        <div className="OrderPaymentSummary__divider" />

        <PaymentSummaryRow
          label="Order Subtotal"
          value={formatPrice(subtotal.currency, subtotal.price)}
        />

        {discounts.price > 0 && (
          <PaymentSummaryRow
            label="Gift Code"
            value={formatPrice(discounts.currency, -discounts.price)}
            tooltip={
              costItemGiftCopy
                ? 'Gift Codes are only applicable to the retail price of items in the order total. They are not applicable to any additional charges included in the order subtotal.'
                : null
            }
          />
        )}

        {gst && gst.price > 0 && (
          <PaymentSummaryRow
            label={gstLabel}
            value={formatPrice(gst.currency, gst.price)}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}

        {duty && duty.price > 0 && (
          <PaymentSummaryRow
            label={dutyLabel}
            value={formatPrice(duty.currency, duty.price)}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}

        {vat && vat.price > 0 && (
          <PaymentSummaryRow
            label={vatLabel}
            value={formatPrice(vat.currency, vat.price)}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}

        {salesTax && salesTax.price > 0 && (
          <PaymentSummaryRow
            label={salesTaxLabel}
            value={formatPrice(salesTax.currency, salesTax.price)}
            tooltip={TAXES_TOOLTIP_COPY}
          />
        )}

        <PaymentSummaryRow
          label="Shipping &amp; Packaging"
          value={formatPrice(shippingAndPackaging.currency, shippingAndPackaging.price)}
        />

        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, total.price)} />

        <div className="OrderPaymentSummary__divider" />

        <Text treatment="bold1">Payment Details</Text>

        {transactions.map((transaction, index) => (
          <PaymentSummaryRow
            key={index}
            label={
              <Stack space={1} direction="horizontal" alignItems="center">
                <Text>{transaction.details}</Text>
                {transaction.source === 'amazon' && (
                  <img className="OrderPaymentSummary__logo" alt="Amazon" src="/amazon_logo.png" />
                )}
                {transaction.source === 'paypal' && (
                  <img className="OrderPaymentSummary__logo" alt="PayPal" src="/paypal.svg" />
                )}
                {transaction.source === 'affirm' && (
                  <img className="OrderPaymentSummary__logo" alt="Affirm" src="/affirm_small.png" />
                )}
                {transaction.source === 'apple' && (
                  <img className="OrderPaymentSummary__logo" alt="Apple" src="/apple_pay.svg" />
                )}
              </Stack>
            }
            value={transaction.amount}
          />
        ))}

        {Number(siteCreditToBeUsed.price) > 0 && (
          <PaymentSummaryRow
            label="Pay Today(Site Credit)"
            value={formatPrice(siteCreditToBeUsed.currency, siteCreditToBeUsed.price)}
          />
        )}

        {Number(rewardsToBeUsed.price) > 0 && (
          <PaymentSummaryRow
            label="Pay Today(M'O Rewards)"
            value={formatPrice(rewardsToBeUsed.currency, rewardsToBeUsed.price)}
          />
        )}

        {Number(payToday.price) > 0 && (
          <PaymentSummaryRow
            label={
              <Stack space={1} direction="horizontal" alignItems="center">
                <span>Pay Today </span>

                {affirmSecondChargeEnabled ? (
                  <img className="OrderPaymentSummary__logo" alt="Affirm" src="/affirm_small.png" />
                ) : (
                  <>
                    {source === PaymentSource.Amazon && (
                      <img
                        className="OrderPaymentSummary__logo"
                        alt="Amazon"
                        src="/amazon_logo.png"
                      />
                    )}

                    {source === PaymentSource.PayPal && (
                      <img className="OrderPaymentSummary__logo" alt="PayPal" src="/paypal.svg" />
                    )}

                    {source === PaymentSource.Adyen && `(${cardTypeName} ${lastFour})`}
                  </>
                )}
              </Stack>
            }
            value={formatPrice(payToday.currency, payToday.price)}
            hasBillFailure={hasBillFailure}
          />
        )}

        <div className="OrderPaymentSummary__divider" />

        <PaymentSummaryRow
          label="Total Paid"
          value={formatPrice(totalPaid.currency, totalPaid.price)}
        />

        {remainingBalance.price > 0 && (
          <PaymentSummaryRow
            label="Remaining Balance"
            value={formatPrice(remainingBalance.currency, remainingBalance.price)}
            tooltipAnchor="bottomCenter"
            tooltip="Preorder items(including Trunkshow) require a deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
          />
        )}

        {Number(amountToBeCharged.price) > 0 &&
          (!hasPendingAuthorizationItems && source === PaymentSource.Apple ? (
            <OrderDetailsApplePayButton order={orderSummary} />
          ) : (
            <Button
              data-testid="OrderPaymentSummary__payToday"
              onClick={handleButtonClick}
              disabled={isValid === false}
            >
              {`Pay Today ${formatPrice(amountToBeCharged.currency, amountToBeCharged.price)}`}
            </Button>
          ))}

        <Text treatment="body1" className="OrderPaymentSummary__taxes">
          **The amount of sales tax due depends on the sales tax laws of the location to which you
          are shipping
        </Text>

        <Overlay show={loading}>
          <LoadingBalls />
          <Text treatment="eyebrow">Placing order</Text>
        </Overlay>
      </Stack>

      <PayTodayConfirmationModal
        closeModal={handleModalCancel}
        orderNumber={orderNumber}
        show={payTodayModalOpen}
      />
    </>
  );
};
